// theme.ts
import { createTheme } from '@mui/material/styles';

// ---- Rozšíření MUI typů ----
declare module '@mui/material/styles' {
  interface PaletteColor {
    0?: string;
    10?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    1000?: string;
  }

  interface SimplePaletteColorOptions {
    0?: string;
    10?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    1000?: string;
  }

  interface Palette {
    neutral: PaletteColor;
  }
  interface PaletteOptions {
    neutral?: SimplePaletteColorOptions;
  }
  interface TypeBackground {
    alt: string;
  }
}

// ---- color design tokens export ----
export const tokensDark = {
  grey: {
    0: '#ffffff',
    10: '#f6f6f6',
    50: '#f0f0f0',
    100: '#e0e0e0',
    200: '#c2c2c2',
    300: '#a3a3a3',
    400: '#858585',
    500: '#666666',
    600: '#525252',
    700: '#3d3d3d',
    800: '#292929',
    900: '#141414',
    1000: '#000000',
  },
  primary: {
    100: '#d3d4de',
    200: '#a6a9be',
    300: '#7a7f9d',
    400: '#4d547d',
    500: '#21295c',
    600: '#191F45',
    700: '#141937',
    800: '#0d1025',
    900: '#070812',
  },
  secondary: {
    50: '#f0f0f0',
    100: '#fff6e0',
    200: '#ffedc2',
    300: '#ffe3a3',
    400: '#ffda85',
    500: '#ffd166',
    600: '#cca752',
    700: '#997d3d',
    800: '#665429',
    900: '#332a14',
  },
} as const;

type ColorShades = Record<number, string>;

function reverseTokens<T extends Record<string, ColorShades>>(tokens: T): T {
  const reversedTokens: Partial<T> = {};

  (Object.keys(tokens) as Array<keyof T>).forEach((key) => {
    const val = tokens[key];
    const keys = Object.keys(val).map((k) => Number(k) as keyof typeof val);
    const length = keys.length;

    const reversedObj = {} as typeof val;

    for (let i = 0; i < length; i++) {
      reversedObj[keys[i]] = val[keys[length - i - 1]];
    }

    reversedTokens[key] = reversedObj;
  });

  return reversedTokens as T;
}

export const tokensLight = reverseTokens(tokensDark);

type ThemeMode = 'light' | 'dark';

export const themeSettings = (mode: ThemeMode) => {
  return {
    palette: {
      mode,
      ...(mode === 'dark'
        ? {
            primary: {
              ...tokensDark.primary,
              main: tokensDark.primary[400],
              light: tokensDark.primary[400],
            },
            secondary: {
              ...tokensDark.secondary,
              main: tokensDark.secondary[300],
            },
            neutral: {
              ...tokensDark.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: tokensDark.primary[600],
              alt: tokensDark.primary[500],
            },
          }
        : {
            primary: {
              ...tokensLight.primary,
              main: tokensDark.grey[50],
              light: tokensDark.grey[100],
            },
            secondary: {
              ...tokensLight.secondary,
              main: tokensDark.secondary[600],
              light: tokensDark.secondary[700],
            },
            neutral: {
              ...tokensLight.grey,
              main: tokensDark.grey[500],
            },
            background: {
              default: tokensDark.grey[0],
              alt: tokensDark.grey[50],
            },
          }),
    },
    typography: {
      fontFamily: ['Inter', 'sans-serif'].join(','),
      fontSize: 12,
      h1: { fontFamily: ['Inter', 'sans-serif'].join(','), fontSize: 40 },
      h2: { fontFamily: ['Inter', 'sans-serif'].join(','), fontSize: 32 },
      h3: { fontFamily: ['Inter', 'sans-serif'].join(','), fontSize: 24 },
      h4: { fontFamily: ['Inter', 'sans-serif'].join(','), fontSize: 20 },
      h5: { fontFamily: ['Inter', 'sans-serif'].join(','), fontSize: 16 },
      h6: { fontFamily: ['Inter', 'sans-serif'].join(','), fontSize: 14 },
    },
  };
};

// ---- finální theme instance ----
export const theme = (mode: ThemeMode) => createTheme(themeSettings(mode));
